import React, { useState, useEffect } from "react";
import Table from "react-bootstrap/Table";
import { dateFormat } from "../../utils";
import { BILLING_STATUS_LABELS, TIMELOG_STATUS_LABELS } from "../../seedData";
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from "@mui/material";
import { Base64 } from "js-base64";
import ReusableModal from "../ModalContent/ReusableModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faSpinner } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { DELETE_TRIP, EDIT_TRIP_MILES } from "../../constant";
import { Dialog } from "primereact/dialog";
import Swal from "../../messageBox/customSweetAlert";
import { useDispatch } from "react-redux";
import { setLoading } from "../../redux/loaderAction";

const SAME_PAYER_APPROVAL_ERROR =
  "System not allowed to submit different 'Payor' in one submission. Kindly select unique 'Payor' for billing.";

const PRIMARY_COLOR = "#F4A442";
const SECONDARY_COLOR = "#2D5A50";

export function validateSelectedClaimsSamePayer(tableData, selectedScheduleIds) {
  if (!Array.isArray(selectedScheduleIds) || selectedScheduleIds.length === 0) {
    return { valid: true };
  }

  const selectedIdSet = new Set(
    selectedScheduleIds
      .map((id) => (id != null ? String(id) : null))
      .filter(Boolean),
  );

  const getPayerKey = (item) => {
    const payerDetails = item?.passenger?.passenger?.payerDetails;
    const payerId = payerDetails?.id;
    if (payerId != null && payerId !== "") {
      return String(payerId);
    }
    const payerName = payerDetails?.payer_name?.trim?.();
    if (payerName) {
      return payerName.toLowerCase();
    }
    return "__MISSING__";
  };

  const payerKeys = [];
  (tableData ?? []).forEach((item) => {
    const scheduleId = item?.schedule_id;
    if (scheduleId == null || !selectedIdSet.has(String(scheduleId))) {
      return;
    }
    payerKeys.push(getPayerKey(item));
  });

  if (payerKeys.length === 0) {
    return { valid: true };
  }

  const uniquePayers = new Set(payerKeys);
  if (uniquePayers.size > 1) {
    return { valid: false, message: SAME_PAYER_APPROVAL_ERROR };
  }

  return { valid: true };
}

function BillingTable({
  tableData,
  onGetCheckedItems,
  selectedRows,
  setSelectedRows,
  pagination,
  onPageChange,
}) {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Local state to manage table data for immediate UI updates
  const [localTableData, setLocalTableData] = useState(tableData);
  const usesServerPagination = pagination != null;

  // Sync local state with prop when tableData changes
  useEffect(() => {
    setLocalTableData(tableData);
    if (!usesServerPagination) {
      setCurrentPage(1);
    }
  }, [tableData, usesServerPagination]);

  const totalPages = usesServerPagination
    ? Math.max(1, pagination.totalPages ?? 1)
    : Math.ceil(localTableData.length / itemsPerPage) || 1;
  const activePage = usesServerPagination
    ? (pagination.currentPage ?? 1)
    : currentPage;
  const recordsPerPage = usesServerPagination
    ? (pagination.recordsPerPage ?? itemsPerPage)
    : itemsPerPage;
  const startIndex = (activePage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedData = usesServerPagination
    ? localTableData
    : localTableData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (usesServerPagination && typeof onPageChange === "function") {
      onPageChange(page);
      return;
    }
    setCurrentPage(page);
  };

  const totalRecords = usesServerPagination
    ? (pagination.totalRecords ?? localTableData.length)
    : localTableData.length;
  const displayEndIndex = Math.min(
    startIndex + (paginatedData?.length || 0),
    totalRecords,
  );

  const getPageNumbers = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "ellipsis", totalPages];
  };

  const pageNumbers = getPageNumbers();

  // Common rounding function
  const customRound = (value) => {
    const decimal = value % 1;
    const integer = Math.floor(value);

    if (decimal > 0.5) {
      return integer + 1;
    } else {
      return integer;
    }
  };

  const [checkedItems, setCheckedItems] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [openReasonsPopup, setOpenReasonsPopup] = useState(false);
  const [reasonsData, setReasonsData] = useState([]);
  const [editMilesDialogVisible, setEditMilesDialogVisible] = useState(false);
  const [editMilesData, setEditMilesData] = useState({
    tripId: null,
    scheduleId: null,
    scheduleTransId: null,
    passengerId: null,
    miles: "",
  });
  const [savingMiles, setSavingMiles] = useState(false);

  const getBillingLegDetails = (data) =>
    data?.schedule?.leg_details ?? data?.tripData?.leg_details ?? [];

  const isMatchingBillingLeg = (leg, tripId, scheduleTransId) => {
    const legTripId =
      leg?.schedule_trans_id ?? leg?.trip_id ?? leg?.trip?.trip_id;
    if (
      scheduleTransId != null &&
      leg?.schedule_trans_id != null &&
      String(leg.schedule_trans_id) === String(scheduleTransId)
    ) {
      return true;
    }
    if (tripId != null && legTripId != null && String(legTripId) === String(tripId)) {
      return true;
    }
    return false;
  };

  const updateBillingLegMiles = (legs, tripId, scheduleTransId, milesNum) =>
    (legs ?? []).map((leg) =>
      isMatchingBillingLeg(leg, tripId, scheduleTransId)
        ? { ...leg, miles: milesNum }
        : leg,
    );

  const sumBillingLegMiles = (legs) =>
    (legs ?? []).reduce((sum, leg) => {
      const legMiles =
        leg?.miles != null && leg?.miles !== "" ? parseFloat(leg.miles) : 0;
      return sum + (Number.isFinite(legMiles) ? legMiles : 0);
    }, 0);

  const applyMilesUpdateToRow = (item, tripId, scheduleTransId, scheduleId, milesNum) => {
    if (String(item?.schedule_id) !== String(scheduleId)) {
      return item;
    }

    const updatedScheduleLegs = updateBillingLegMiles(
      item?.schedule?.leg_details,
      tripId,
      scheduleTransId,
      milesNum,
    );
    const updatedTripLegs = updateBillingLegMiles(
      item?.tripData?.leg_details ?? item?.schedule?.leg_details,
      tripId,
      scheduleTransId,
      milesNum,
    );
    const totalMiles = sumBillingLegMiles(updatedScheduleLegs);

    return {
      ...item,
      miles: totalMiles,
      schedule_trans: item?.schedule_trans
        ? { ...item.schedule_trans, miles: totalMiles }
        : item.schedule_trans,
      schedule: item?.schedule
        ? {
            ...item.schedule,
            leg_details: updatedScheduleLegs,
            total_miles: totalMiles,
          }
        : item.schedule,
      tripData: item?.tripData
        ? {
            ...item.tripData,
            leg_details: updatedTripLegs,
            total_miles: totalMiles,
          }
        : item.tripData,
    };
  };

  const getNemtRates = (data) => {
    const rate = data?.schedule?.nemtServiceRate ?? {};
    return {
      baseBillRate: rate?.base_bill_rate ?? "",
      mileageBillRate: rate?.mileage_bill_rate ?? "",
    };
  };

  const handleCheckboxChange = (item) => {
    setCheckedItems((prev) => {
      const isChecked = prev.includes(item);
      if (isChecked) {
        return prev.filter((i) => i !== item);
      } else {
        return [...prev, item];
      }
    });
    setSelectedRows([]);
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    const currentPageIds = paginatedData.map((data) => data.schedule_id);
    const allSelected = currentPageIds.every((id) => checkedItems.includes(id));

    if (allSelected) {
      // Deselect all items on current page
      setCheckedItems((prev) =>
        prev.filter((id) => !currentPageIds.includes(id)),
      );
    } else {
      // Select all items on current page
      setCheckedItems((prev) => {
        const newItems = [...prev];
        currentPageIds.forEach((id) => {
          if (!newItems.includes(id)) {
            newItems.push(id);
          }
        });
        return newItems;
      });
    }
    setSelectedRows([]);
  };

  // Check if all items on current page are selected
  const areAllSelected =
    paginatedData.length > 0 &&
    paginatedData.every((data) => checkedItems.includes(data.schedule_id));

  // Check if some (but not all) items on current page are selected
  const areSomeSelected =
    paginatedData.some((data) => checkedItems.includes(data.schedule_id)) &&
    !areAllSelected;
  useEffect(() => {
    setCurrentPage(1);
  }, [localTableData]);

  useEffect(() => {
    if (typeof onGetCheckedItems === "function") {
      onGetCheckedItems(checkedItems, {
        validateSamePayerApproval: () =>
          validateSelectedClaimsSamePayer(localTableData, checkedItems),
      });
    }
  }, [checkedItems, localTableData, onGetCheckedItems]);

  const handleOpenPopup = (data) => {
    setPopupData(data);
    setOpenPopup(true);
  };
  const handleClosePopup = () => {
    setOpenPopup(false);
    setPopupData(null);
  };
  const handleOpenReasons = (reasons) => {
    setReasonsData(Array.isArray(reasons) ? reasons : []);
    setOpenReasonsPopup(true);
  };
  const handleCloseReasons = () => {
    setOpenReasonsPopup(false);
    setReasonsData([]);
  };

  const openEditMilesDialog = (data, leg) => {
    const tripId = leg?.schedule_trans_id ?? leg?.trip_id;
    const miles =
      leg?.miles != null && leg?.miles !== ""
        ? leg.miles
        : data?.schedule_trans?.miles;
    if (!tripId) return;
    const passengerId = data?.passenger?.passengerId ?? null;
    setEditMilesData({
      tripId,
      scheduleId: data.schedule_id,
      scheduleTransId: leg?.schedule_trans_id,
      passengerId,
      miles: miles != null && miles !== "" ? String(miles) : "",
    });
    setEditMilesDialogVisible(true);
  };

  const handleEditMilesChange = (value) => {
    const numericValue = value.replace(/[^\d.]/g, "");
    setEditMilesData((prev) => ({ ...prev, miles: numericValue }));
  };

  const handleSaveMiles = async () => {
    if (!editMilesData.tripId) {
      setEditMilesDialogVisible(false);
      return;
    }
    if (!editMilesData.passengerId) {
      Swal.fire({
        title: "Error",
        text: "Passenger is missing for this trip.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    const milesStr = (editMilesData.miles || "").trim();
    if (!milesStr) {
      Swal.fire({
        title: "Error",
        text: "Miles is required.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    const milesNum = parseFloat(milesStr);
    if (Number.isNaN(milesNum) || milesNum < 0) {
      Swal.fire({
        title: "Error",
        text: "Miles must be a valid number (0 or greater).",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    dispatch(setLoading(true));
    setSavingMiles(true);
    try {
      const response = await axios.patch(
        `${EDIT_TRIP_MILES}/${editMilesData.tripId}`,
        {
          miles: String(milesNum),
          payer: editMilesData.payer,
          passengerId: Number(editMilesData.passengerId),
        },
      );
      if (response?.data?.status === "200" || response?.data?.status === 200) {
        Swal.fire({
          title: "Success!",
          text: response?.data?.message || "Trip miles updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
        setEditMilesDialogVisible(false);
        setLocalTableData((prevData) =>
          prevData.map((item) =>
            applyMilesUpdateToRow(
              item,
              editMilesData.tripId,
              editMilesData.scheduleTransId,
              editMilesData.scheduleId,
              milesNum,
            ),
          ),
        );
      } else {
        Swal.fire({
          title: "Error",
          text: response?.data?.message || "Failed to update trip miles.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update trip miles.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      dispatch(setLoading(false));
      setSavingMiles(false);
    }
  };

  const handleDeleteLeg = (tripId, leg, data, legIndex) => {
    //alert(tripId);S
    //return false;S
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this leg!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(setLoading(true));
        axios
          .delete(`${DELETE_TRIP}/${tripId}/schedule/${leg.schedule_trans_id}`)
          .then((response) => {
            if (response.data.status === 200) {
              dispatch(setLoading(false));

              // Remove the leg from local state immediately
              setLocalTableData((prevData) => {
                return prevData
                  .map((item) => {
                    // Find the trip that contains this leg
                    if (
                      item.schedule_id === data.schedule_id &&
                      item.tripData?.leg_details
                    ) {
                      // Remove the leg by index (most reliable) or by matching trip_id
                      const updatedLegDetails =
                        item.tripData.leg_details.filter((l, index) => {
                          // First try to match by index if legIndex is provided
                          if (legIndex !== undefined && index === legIndex) {
                            return false; // Remove this leg
                          }
                          // Fallback: match by trip_id or schedule_trans_id
                          const legTripId =
                            l.trip_id || l.trip?.trip_id || l.schedule_trans_id;
                          const deletedTripId =
                            leg.trip_id ||
                            leg.trip?.trip_id ||
                            leg.schedule_trans_id;
                          return (
                            legTripId !== deletedTripId && legTripId !== tripId
                          );
                        });

                      // If no legs remain, mark this item for removal
                      if (updatedLegDetails.length === 0) {
                        return null; // Mark for removal
                      }

                      // Update the trip data with filtered legs
                      const updatedTripData = {
                        ...item.tripData,
                        leg_details: updatedLegDetails,
                      };

                      // Update no_of_leg if it exists
                      const updatedSchedule = item.schedule
                        ? {
                            ...item.schedule,
                            no_of_leg: updatedLegDetails.length,
                          }
                        : item.schedule;

                      return {
                        ...item,
                        tripData: updatedTripData,
                        schedule: updatedSchedule,
                      };
                    }
                    return item;
                  })
                  .filter((item) => item !== null); // Remove items marked for deletion (no legs remaining)
              });

              Swal.fire({
                title: "Success!",
                text: response.data.message || "Leg deleted successfully",
                icon: "success",
                confirmButtonText: "OK",
              });
            } else {
              dispatch(setLoading(false));
              Swal.fire({
                title: "Error!",
                text: response.data.message || "Failed to delete leg",
                icon: "error",
                confirmButtonText: "OK",
              });
            }
          })
          .catch((error) => {
            dispatch(setLoading(false));
            Swal.fire({
              title: "Error!",
              text:
                error.response?.data?.message ||
                error.message ||
                "An error occurred while deleting the leg",
              icon: "error",
              confirmButtonText: "OK",
            });
          });
      }
    });
  };

  let totalMiles = 0;
  let totalTripAmount = 0;
  let totalReceivedAmount = 0;

  return (
    <>
      <Table striped bordered hover className="tbl-require">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={areAllSelected}
                ref={(input) => {
                  if (input) input.indeterminate = areSomeSelected;
                }}
                onChange={handleSelectAll}
                style={{ cursor: "pointer" }}
                title={areAllSelected ? "Deselect all" : "Select all"}
              />
            </th>
            {/* <th>SlNo</th> */}
            <th>Date</th>
            <th>Billed Date</th>
            <th>Trip Status</th>
            <th>Valid</th>
            <th>Passenger</th>
            {/* <th>Vehicle</th> */}
            <th>Legs</th>
            <th>Dead Head Miles</th>
            <th>Service Information</th>
            <th>Payor</th>
            <th>Total Miles</th>
            <th>Trip Amount</th>
            <th>Received Amount</th>
            <th>Billing Status</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.length === 0 ? (
            <tr>
              <td colSpan={14} style={{ textAlign: "center", color: "gray" }}>
                No data available
              </td>
            </tr>
          ) : (
            paginatedData.map(
              (data, index) => (
                (totalMiles += Number(
                  data.tripData?.total_miles ?? data.miles ?? 0,
                )),
                (totalTripAmount += Number(data.schedule?.bill_amount || 0)),
                (totalReceivedAmount += Number(
                  data.schedule?.received_amount || 0,
                )),
                (
                  <tr
                    key={index}
                    className={
                      selectedRows.includes(data.schedule_id)
                        ? "highlight-row"
                        : ""
                    }
                  >
                    <td>
                      <input
                        type="checkbox"
                        value={data.schedule_id}
                        checked={checkedItems.includes(data.schedule_id)}
                        onChange={() => handleCheckboxChange(data.schedule_id)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    {/* <td>{(currentPage - 1) * itemsPerPage + index + 1}</td> */}
                    <td>{dateFormat(data.trip_date)}</td>
                    <td style={{ textAlign: "center" }}>
                      {data?.schedule?.bill_date ? (
                        dateFormat(data?.schedule?.bill_date)
                      ) : (
                        <span style={{ color: "#b1acac" }}>N/A</span>
                      )}
                    </td>
                    <td>
                      <span
                        style={{
                          color:
                            TIMELOG_STATUS_LABELS[
                              data.schedule?.time_log_status
                            ] === "Confirmed"
                              ? "#28a745"
                              : "#C82333",
                        }}
                      >
                        {TIMELOG_STATUS_LABELS[
                          data.schedule?.time_log_status
                        ] || ""}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {Array.isArray(data?.billing_reasons) &&
                      data.billing_reasons.length > 0 ? (
                        <span
                          title="Has billing reasons"
                          style={{
                            color: "#dc3545",
                            fontSize: "22px",
                            cursor: "pointer",
                            fontWeight: "bold",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenReasons(data.billing_reasons);
                          }}
                        >
                          &#215;
                        </span>
                      ) : (
                        <span
                          title="No billing reasons"
                          style={{
                            color: "#28a745",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                        >
                          &#10004;
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <a
                          href={`/home/passenger/profile/id?profilId=${Base64.encode(
                            data?.passenger?.passengerId,
                          )}`}
                          target="_blank" // Open in a new tab
                          onClick={() =>
                            localStorage.setItem("activeMenu", "Passenger List")
                          }
                          rel="noopener noreferrer" // Security best practice
                          style={{ textDecoration: "none", color: "#31876f" }} // Optional: Style the link
                        >
                          {`${data.passenger?.firstName || ""}` +
                            " " +
                            `${data.passenger?.middleName || ""}` +
                            " " +
                            `${data.passenger?.lastName || ""}`}
                        </a>
                      </div>
                    </td>
                    {/* <td>{data.vehicle?.vehicle_name}</td> */}
                    <td
                      style={{ textAlign: "center" }}
                      onClick={() => {
                        handleOpenPopup(data);
                      }}
                    >
                      {data.schedule?.no_of_leg}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {parseInt(data.schedule?.dead_head_miles ?? 0) ?? "N/A"}
                    </td>
                    <td>
                      {(() => {
                        const legDetails = getBillingLegDetails(data);
                        const { baseBillRate, mileageBillRate } =
                          getNemtRates(data);
                        const driverNames = [
                          ...new Set(
                            legDetails
                              .map((leg) => leg?.driver?.fullName)
                              .filter((name) => name != null && name !== ""),
                          ),
                        ].join(", ");

                        return (
                          <>
                            {legDetails.map((leg, legIndex) => {
                              const milesToUse =
                                leg?.miles != null && leg?.miles !== ""
                                  ? leg.miles
                                  : (data?.schedule_trans?.miles ?? "N/A");
                              const baseCodeRate = parseFloat(
                                leg?.base_bill_rate ?? baseBillRate ?? 0,
                              );
                              const mileageAmount =
                                parseFloat(
                                  leg?.mileage_bill_rate ??
                                    mileageBillRate ??
                                    0,
                                ) * parseFloat(milesToUse || 0);

                              const roundedBaseRate = baseCodeRate;
                              const roundedMileageAmount = mileageAmount;
                              const totalAmount =
                                roundedBaseRate + roundedMileageAmount;

                              const legRowKey = [
                                data.schedule_id,
                                leg.schedule_trans_id,
                                leg.trip_id,
                                leg.leg_number,
                                legIndex,
                              ]
                                .filter((v) => v != null && v !== "")
                                .join("-");

                              return (
                                <div
                                  key={legRowKey}
                                  style={{
                                    position: "relative",
                                    marginBottom: "15px",
                                    paddingRight: "25px",
                                  }}
                                >
                                  <div
                                    style={{ marginBottom: "10px !important" }}
                                  >
                                    <span style={{ fontWeight: "bold" }}>
                                      Driver -{" "}
                                    </span>
                                    <span>{driverNames}</span> <br />
                                    <span style={{ fontWeight: "bold" }}>
                                      Leg -{" "}
                                    </span>
                                    <span>
                                      {leg.leg_number} - {leg.base_code}($
                                      {roundedBaseRate}) - {leg.milage_code}($
                                      {roundedMileageAmount.toFixed(2)})
                                    </span>
                                    <br />
                                    <span style={{ fontWeight: "bold" }}>
                                      Service -
                                    </span>
                                    <span style={{ fontWeight: "lighter" }}>
                                      {" "}
                                      {leg.nemt_service_name}
                                    </span>
                                  </div>

                                  <div style={{ marginTop: "10px !important" }}>
                                    <span style={{ fontWeight: "bold" }}>
                                      Miles -
                                    </span>
                                    <span style={{ fontWeight: "lighter" }}>
                                      {" "}
                                      {milesToUse}
                                    </span>
                                  </div>
                                  {/* <div><span style={{fontWeight:'bold',display:'none'}}>Modifier -</span><span style={{fontWeight:'lighter'}}> {leg.nemt_service_name}</span></div> */}
                                  <div
                                    style={{
                                      color: "green",
                                      fontWeight: "normal",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    Total Amount - ${totalAmount.toFixed(2)}
                                  </div>

                                  <i
                                    className="pi pi-pen-to-square icon-btn-edit cursor-pointer"
                                    title="Edit miles"
                                    style={{
                                      fontSize: "13px",
                                      position: "absolute",
                                      right: "0",
                                      top: "45px",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditMilesDialog(data, leg);
                                    }}
                                  ></i>

                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    style={{
                                      position: "absolute",
                                      right: "5px",
                                      top: "65px",
                                      color: "#dc3545",
                                      cursor: "pointer",
                                      fontSize: "14px",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const tripId =
                                        leg.trip_id ??
                                        leg.trip?.trip_id ??
                                        data.trip_id;
                                      if (tripId) {
                                        handleDeleteLeg(
                                          tripId,
                                          leg,
                                          data,
                                          legIndex,
                                        );
                                      } else {
                                        Swal.fire({
                                          title: "Error!",
                                          text: "Trip ID not found. Cannot delete this leg.",
                                          icon: "error",
                                          confirmButtonText: "OK",
                                        });
                                        console.error(
                                          "Trip ID not found in leg:",
                                          leg,
                                        );
                                      }
                                    }}
                                    title="Delete leg"
                                  />
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      {data?.passenger?.passenger?.payerDetails?.payer_name ??
                        "N/A"}
                    </td>

                    <td>
                      {data?.schedule?.total_miles ??
                        data?.tripData?.total_miles ??
                        data?.schedule_trans?.miles ??
                        data?.miles ??
                        "N/A"}
                    </td>
                    <td>
                      {(() => {
                        const { baseBillRate, mileageBillRate } =
                          getNemtRates(data);
                        const totalTripAmount =
                          getBillingLegDetails(data).reduce((total, leg) => {
                            const milesToUse =
                              leg?.miles != null && leg?.miles !== ""
                                ? leg.miles
                                : data?.schedule_trans?.miles;
                            const baseRate = parseFloat(
                              leg?.base_bill_rate ?? baseBillRate ?? 0,
                            );
                            const mileageAmount =
                              parseFloat(
                                leg?.mileage_bill_rate ?? mileageBillRate ?? 0,
                              ) * parseFloat(milesToUse || 0);

                            // const roundedBaseRate = customRound(baseRate);
                            const roundedBaseRate = baseRate;
                            // const roundedMileageAmount =
                            //   customRound(mileageAmount);
                            const roundedMileageAmount = mileageAmount;
                            const legTotal =
                              roundedBaseRate + roundedMileageAmount;

                            return total + legTotal;
                          }, 0) || 0;

                        return totalTripAmount
                          ? `$${totalTripAmount.toFixed(2)}`
                          : "";
                      })()}
                    </td>
                    <td> {`$${data.schedule?.received_amount ?? 0}`}</td>
                    <td>
                      {BILLING_STATUS_LABELS[data.schedule?.billing_status]}
                    </td>
                  </tr>
                )
              ),
            )
          )}
        </tbody>
        {paginatedData.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={6} style={{ textAlign: "right" }}></td>
              <td colSpan={3} style={{ textAlign: "center" }}>
                Total
              </td>
              <td>
                <strong>{Number(totalMiles.toFixed(2))}</strong>
              </td>
              <td>
                <strong>{"$" + totalTripAmount}</strong>
              </td>
              <td>
                <strong>{`$${totalReceivedAmount}`}</strong>
              </td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </Table>

      {totalPages > 1 && (
        <div
          className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 px-4 py-3 mt-0"
          style={{
            backgroundColor: "rgba(248, 249, 250, 0.8)",
            border: "1px solid #e9ecef",
            borderTop: "none",
            borderRadius: "0 0 0.25rem 0.25rem",
          }}
        >
          <p className="text-sm text-muted mb-0">
            Showing{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {paginatedData?.length ? startIndex + 1 : 0}-{displayEndIndex}
            </span>{" "}
            of{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {totalRecords}
            </span>{" "}
            results
          </p>
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: activePage === 1 ? 0.35 : 1 }}
              disabled={activePage === 1}
              onClick={() => handlePageChange(1)}
              title="First page"
              aria-label="First page"
            >
              <i className="pi pi-angle-double-left" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none me-1"
              style={{ opacity: activePage === 1 ? 0.35 : 1 }}
              disabled={activePage === 1}
              onClick={() => handlePageChange(activePage - 1)}
              title="Previous page"
              aria-label="Previous page"
            >
              <i className="pi pi-angle-left" />
            </button>
            <div className="d-flex align-items-center gap-1">
              {pageNumbers.map((p, i) =>
                p === "ellipsis" ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-muted">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className="btn btn-sm fw-semibold rounded"
                    style={{
                      minWidth: 32,
                      height: 32,
                      backgroundColor:
                        p === activePage ? PRIMARY_COLOR : "transparent",
                      color: p === activePage ? "#fff" : "#495057",
                      border: "none",
                      boxShadow:
                        p === activePage
                          ? "0 1px 2px rgba(0,0,0,0.05)"
                          : "none",
                    }}
                    onClick={() => handlePageChange(p)}
                    aria-label={`Page ${p}`}
                    aria-current={p === activePage ? "page" : undefined}
                  >
                    {p}
                  </button>
                ),
              )}
            </div>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none ms-1"
              style={{ opacity: activePage === totalPages ? 0.35 : 1 }}
              disabled={activePage === totalPages}
              onClick={() => handlePageChange(activePage + 1)}
              title="Next page"
              aria-label="Next page"
            >
              <i className="pi pi-angle-right" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: activePage === totalPages ? 0.35 : 1 }}
              disabled={activePage === totalPages}
              onClick={() => handlePageChange(totalPages)}
              title="Last page"
              aria-label="Last page"
            >
              <i className="pi pi-angle-double-right" />
            </button>
          </div>
        </div>
      )}
      <ReusableModal
        open={openPopup}
        onClose={handleClosePopup}
        title={
          <div
            style={{
              position: "relative",
              paddingBottom: "10px",
              borderBottom: "1px solid #ddd",
            }}
          >
            <h4 style={{ margin: 0, fontWeight: "normal" }}>Shedule Details</h4>
          </div>
        }
      >
        {popupData ? (
          <TableContainer sx={{ boxShadow: "none" }}>
            <Table sx={{ border: "1px solid #31876f33" }}>
              <TableHead className="table-header">
                <TableRow>
                  <TableCell
                    className="theader-cell"
                    sx={{ fontWeight: "bold", padding: "5px" }}
                  >
                    Leg
                  </TableCell>
                  <TableCell
                    style={{
                      border: "1px solid #d5d2d2",
                      width: "50%",
                      textAlign: "left",
                    }}
                    sx={{ fontWeight: "bold", padding: "5px", color: "#fff" }}
                  >
                    Pickup Address
                  </TableCell>
                  <TableCell
                    className="theader-cell"
                    sx={{ fontWeight: "bold", padding: "5px" }}
                  >
                    Pickup Time
                  </TableCell>
                  <TableCell
                    style={{
                      border: "1px solid #d5d2d2",
                      width: "50%",
                      textAlign: "left",
                    }}
                    sx={{ fontWeight: "bold", padding: "5px", color: "#fff" }}
                  >
                    DropOff Address
                  </TableCell>
                  <TableCell
                    className="theader-cell"
                    sx={{ fontWeight: "bold", padding: "5px" }}
                  >
                    DropOff Time
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {popupData.schedule_tran.map((leg, legArrayIndex) => (
                  <TableRow key={legArrayIndex}>
                    {/* <TableCell>{index + 1}</TableCell> */}
                    <TableCell className="maintenance-tbody-cell">
                      {" "}
                      {legArrayIndex + 1}
                    </TableCell>
                    <TableCell
                      style={{
                        border: "1px solid #d5d2d2",
                        width: "50%",
                        textAlign: "left",
                      }}
                    >
                      {leg.pickupLocation?.pickup_location &&
                        leg.pickupLocation.pickup_location.replace(/,,/, "") +
                          ", "}
                      {leg.pickupLocation?.pickup_address &&
                        leg.pickupLocation.pickup_address.replace(/,,/, "") +
                          ", "}
                      {leg.pickupLocation?.pickup_city &&
                        leg.pickupLocation.pickup_city.replace(/,,/, "") + ", "}
                      {leg?.pickupLocation?.state_master_list
                        ?.pickup_state_name &&
                        leg?.pickupLocation?.state_master_list?.pickup_state_name.replace(
                          /,,/,
                          "",
                        ) + ", "}
                      {leg?.pickupLocation?.pickup_zip || ""}
                    </TableCell>
                    <TableCell className="maintenance-tbody-cell">
                      {leg.pick_up_time}
                    </TableCell>
                    <TableCell
                      style={{
                        border: "1px solid #d5d2d2",
                        width: "50%",
                        textAlign: "left",
                      }}
                    >
                      {leg.dropoffLocation?.dropoff_location &&
                        leg.dropoffLocation?.dropoff_location.replace(
                          /,,/,
                          "",
                        ) + ", "}
                      {leg.dropoffLocation?.dropoff_address &&
                        leg.dropoffLocation?.dropoff_address.replace(/,,/, "") +
                          ", "}
                      {leg.dropoffLocation?.dropoff_city &&
                        leg.dropoffLocation?.dropoff_city.replace(/,,/, "") +
                          ", "}
                      {leg?.dropoffLocation?.state_master_list
                        ?.dropOff_state_name &&
                        leg?.dropoffLocation?.state_master_list?.dropOff_state_name.replace(
                          /,,/,
                          "",
                        ) + ", "}
                      {leg.dropoffLocation?.dropoff_zip}
                    </TableCell>
                    <TableCell className="maintenance-tbody-cell">
                      {leg.drop_off_time}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div>No legs available</div> // Handle the case where there are no legs
        )}
      </ReusableModal>
      <ReusableModal
        open={openReasonsPopup}
        onClose={handleCloseReasons}
        title={
          <div
            style={{
              paddingBottom: "8px",
              borderBottom: "1px solid #e8e8e8",
              width: "100%",
            }}
          >
            <span style={{ fontSize: "1rem", fontWeight: 600, color: "#333" }}>
              Billing Errors
            </span>
          </div>
        }
      >
        <div
          style={{
            padding: "8px 0",
            minWidth: 280,
            textAlign: "left",
            width: "100%",
          }}
        >
          {Array.isArray(reasonsData) && reasonsData.length > 0 ? (
            <ul
              style={{
                margin: 0,
                paddingLeft: "20px",
                listStyleType: "disc",
                fontSize: "0.875rem",
                lineHeight: 1.6,
                color: "#b91c1c",
                textAlign: "left",
                width: "100%",
              }}
            >
              {reasonsData.map((r, i) => (
                <li
                  key={i}
                  style={{
                    marginBottom: "6px",
                    color: "#b91c1c",
                  }}
                >
                  {typeof r === "string"
                    ? r
                    : (r?.message ?? r?.reason ?? JSON.stringify(r))}
                </li>
              ))}
            </ul>
          ) : (
            <div
              style={{
                fontSize: "0.875rem",
                color: "#666",
              }}
            >
              No billing reasons provided.
            </div>
          )}
        </div>
      </ReusableModal>

      {editMilesDialogVisible && (
        <Dialog
          visible={editMilesDialogVisible}
          header="Edit Miles Traveled"
          onHide={() => setEditMilesDialogVisible(false)}
          style={{ width: "30rem" }}
        >
          <div className="p-fluid">
            <div className="mb-3">
              <label className="form-label">Miles</label>
              <input
                type="text"
                className="form-control"
                value={editMilesData.miles ?? ""}
                onChange={(e) => handleEditMilesChange(e.target.value)}
                placeholder="Enter miles"
                inputMode="numeric"
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEditMilesDialogVisible(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSaveMiles}
                disabled={savingMiles}
              >
                {savingMiles ? (
                  <>
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      style={{ marginRight: "4px" }}
                    />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default BillingTable;
